import Button from "@amzn/meridian/button";
import DatePicker from "@amzn/meridian/date-picker";
import Input from "@amzn/meridian/input";
import Link from "@amzn/meridian/link";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import Textarea from "@amzn/meridian/textarea";
import { get } from "lodash";
import moment from "moment";
import React, { useState } from "react";

import { DEFAULT_DATE_FORMAT } from "../../../utils/formattingUtils";

interface IInputProps {
    prefix?: string;
    suffix?: string;
    type?: string;
    font?: any;
    color?: string;
    key?: string;
    options?: Array<{ value: string; label: string }>;
    editable?: boolean;
}

interface ICellProps {
    value: string;
    onChange: (value: string) => void;
}

interface IInputCellProps extends ICellProps {
    prefix?: string;
    suffix?: string;
    type: "text" | "number";
}

const InputComponent: React.FC<IInputCellProps> = ({ value, prefix, suffix, onChange, type }) => (
    <Input
        value={value}
        prefix={prefix}
        suffix={suffix}
        onChange={onChange}
        type={type}
        size={"small"}
        width={"144px"}
    />
);

const DateComponent: React.FC<ICellProps> = ({ value, onChange }) => (
    <DatePicker
        value={moment(value).format(DEFAULT_DATE_FORMAT) || ""}
        onChange={onChange}
        width={"174px"}
        size={"small"}
    />
);

const CommentComponent: React.FC<ICellProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);

    return (
        <>
            {value ? (
                <>
                    <Text type="b300">{value}</Text>
                    <Link onClick={handleOpen}>Edit</Link>
                </>
            ) : (
                <Link onClick={handleOpen}>Add</Link>
            )}

            <Modal
                title="Add/Edit comments"
                open={isOpen}
                onClose={handleClose}
                scrollContainer="viewport"
                closeLabel="Close"
                aria-describedby="modal-description"
                width="400px"
            >
                <Textarea value={value} onChange={onChange} />
                <ModalFooter>
                    <Row alignmentHorizontal="end" widths="fit">
                        <Button type="secondary" size="small" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="primary" size="small" onClick={handleClose}>
                            Confirm
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};

const SelectComponent: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
}> = ({ value, onChange, options }) => (
    <Select value={value} onChange={onChange} size={"small"} width={"174px"}>
        {options.map(option => (
            <SelectOption key={option.value} label={option.label} value={option.value} />
        ))}
    </Select>
);

export const renderInput = (
    inputProps: IInputProps,
    data: any,
    handleInputChange: (key: string, value: any) => void
) => {
    const value = get(data, inputProps.key, "");
    const handleChange = (newValue: any) => handleInputChange(inputProps.key, newValue);

    const inputComponents = {
        inputString: () => (
            <Row alignmentHorizontal={"end"}>
                <InputComponent
                    value={value}
                    type="text"
                    prefix={inputProps.prefix}
                    suffix={inputProps.suffix}
                    onChange={handleChange}
                />
            </Row>
        ),
        inputNumber: () => (
            <Row alignmentHorizontal={"end"}>
                <InputComponent
                    value={value}
                    type="number"
                    prefix={inputProps.prefix}
                    suffix={inputProps.suffix}
                    onChange={handleChange}
                />
            </Row>
        ),
        dropdown: () => (
            <Row alignmentHorizontal={"end"}>
                <SelectComponent
                    value={value}
                    onChange={handleChange}
                    options={inputProps.options}
                />
            </Row>
        ),
        comment: () => <CommentComponent value={value} onChange={handleChange} />,
        date: () => (
            <Row alignmentHorizontal={"end"}>
                <DateComponent value={value} onChange={handleChange} />
            </Row>
        ),

        ignore: () => null,
    };

    return inputComponents[inputProps.type]?.() ?? null;
};
